import { useMemo } from 'react';
import axios from 'axios';
import { baseUrl } from './useEndpoints';
import { IInjury, IActiveInjurySummary, ICreateInjuryDto, IUpdateInjuryDto, IRegisterReturnDto, IInjuryNoteDto, IAthleteCapabilities, ITeamCapabilities, IInsightsMetric } from '../models/injury';
import { ATHLETE_INJURY_METRIC, TEAM_INJURY_METRIC } from '../enums/injuries';
import { IAnamnesis, IUpsertAnamnesisDto } from '../models/anamnesis';

export const useInjuryEndpoints = () =>
  useMemo(
    () => ({
      // ─── Injuries ────────────────────────────────────────────────────────
      postInjury: (dto: ICreateInjuryDto) =>
        axios.post<IInjury>(`${baseUrl}/injuries`, dto),

      patchInjury: (id: string, dto: IUpdateInjuryDto) =>
        axios.patch<IInjury>(`${baseUrl}/injuries/${id}`, dto),

      registerInjuryReturn: (id: string, dto: IRegisterReturnDto) =>
        axios.post<IInjury>(`${baseUrl}/injuries/${id}/return`, dto),

      postInjuryNote: (id: string, dto: IInjuryNoteDto) =>
        axios.post<IInjury>(`${baseUrl}/injuries/${id}/notes`, dto),

      getAthleteInjuries: (athleteId: string, params?: { period?: string }) =>
        axios.get<IInjury[]>(`${baseUrl}/injuries/athlete/${athleteId}`, { params }),

      getTeamInjuries: (teamId: string, params?: { period?: string }) =>
        axios.get<IActiveInjurySummary[]>(`${baseUrl}/injuries/team/${teamId}`, { params }),

      getMyInjuries: () =>
        axios.get<IInjury[]>(`${baseUrl}/injuries/me`),

      getMyActiveInjury: () =>
        axios.get<IInjury | null>(`${baseUrl}/injuries/me/active`),

      // ─── Stats / Metrics ─────────────────────────────────────────────────
      getAthleteInjuryMetric: (athleteId: string, metric: ATHLETE_INJURY_METRIC, params?: { period?: string }) =>
        axios.get<unknown>(`${baseUrl}/injuries/stats/athlete/${athleteId}/${metric}`, { params }),

      getAthleteCapabilities: (athleteId: string) =>
        axios.get<IAthleteCapabilities>(`${baseUrl}/injuries/stats/athlete/${athleteId}/capabilities`),

      // ─── Stats / Metrics Team ────────────────────────────────────────────
      getTeamInjuryMetric: (teamId: string, metric: TEAM_INJURY_METRIC, params?: { period?: string }) =>
        axios.get<unknown>(`${baseUrl}/injuries/stats/team/${teamId}/${metric}`, { params }),

      getTeamCapabilities: (teamId: string) =>
        axios.get<ITeamCapabilities>(`${baseUrl}/injuries/stats/team/${teamId}/capabilities`),

      getTeamInsights: (teamId: string, params?: { period?: string }) =>
        axios.get<IInsightsMetric>(`${baseUrl}/injuries/stats/team/${teamId}/team-insights`, { params }),

      // ─── Anamnesis ───────────────────────────────────────────────────────
      getAnamnesis: (athleteId: string) =>
        axios.get<IAnamnesis | null>(`${baseUrl}/anamnesis/${athleteId}`),

      putAnamnesis: (athleteId: string, dto: IUpsertAnamnesisDto) =>
        axios.put<IAnamnesis>(`${baseUrl}/anamnesis/${athleteId}`, dto),
    }),
    []
  );
